import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabaseClient';
import { useResponsive } from '../hooks/useResponsive';
import { COLORS, SPACING, FONT_SIZE, BORDER_RADIUS, SHADOWS } from '../constants/theme';

const PlayerTagManager = ({ profile }) => {
  const { isDesktop } = useResponsive();
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingTag, setEditingTag] = useState(null);
  const [showBulkAssign, setShowBulkAssign] = useState(false);
  const [selectedTag, setSelectedTag] = useState(null);

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    color: '#3B82F6', // Default blue
  });

  // Predefined color palette
  const colorPalette = [
    { name: 'Blue', value: '#3B82F6' },
    { name: 'Purple', value: '#8B5CF6' },
    { name: 'Pink', value: '#EC4899' },
    { name: 'Red', value: '#EF4444' },
    { name: 'Orange', value: '#F97316' },
    { name: 'Yellow', value: '#EAB308' },
    { name: 'Green', value: '#10B981' },
    { name: 'Teal', value: '#14B8A6' },
    { name: 'Cyan', value: '#06B6D4' },
    { name: 'Gray', value: '#6B7280' },
  ];

  useEffect(() => {
    loadTags();
  }, []);

  const loadTags = async () => {
    try {
      setLoading(true);
      const { data, error: fetchError } = await supabase
        .from('player_tags')
        .select(
          `
          *,
          created_by_profile:profiles!player_tags_created_by_fkey(full_name),
          tag_assignments:player_tag_assignments(count)
        `
        )
        .eq('organization_id', profile.organization_id)
        .order('created_at', { ascending: false });

      if (fetchError) throw fetchError;
      setTags(data || []);
    } catch (err) {
      console.error('Error loading tags:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!formData.name.trim()) {
      setError('Tag name is required');
      return;
    }

    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();

      const tagData = {
        organization_id: profile.organization_id,
        name: formData.name.trim(),
        description: formData.description.trim() || null,
        color: formData.color,
        created_by: user.id,
      };

      if (editingTag) {
        // Update existing tag
        const { error: updateError } = await supabase
          .from('player_tags')
          .update(tagData)
          .eq('id', editingTag);

        if (updateError) throw updateError;
        setSuccess('Tag updated successfully!');
      } else {
        // Create new tag
        const { error: insertError } = await supabase.from('player_tags').insert(tagData);

        if (insertError) throw insertError;
        setSuccess('Tag created successfully!');
      }

      setShowCreateForm(false);
      setEditingTag(null);
      loadTags();

      // Reset form
      setFormData({
        name: '',
        description: '',
        color: '#3B82F6',
      });
    } catch (err) {
      console.error('Error saving tag:', err);
      if (err.message.includes('duplicate')) {
        setError('A tag with this name already exists in your organization');
      } else {
        setError(err.message);
      }
    }
  };

  const handleEdit = (tag) => {
    setEditingTag(tag.id);
    setFormData({
      name: tag.name,
      description: tag.description || '',
      color: tag.color,
    });
    setShowCreateForm(true);
    setError('');
    setSuccess('');
  };

  const handleDelete = async (tagId) => {
    if (
      !window.confirm(
        'Are you sure you want to delete this tag? This will remove it from all players.'
      )
    ) {
      return;
    }

    try {
      const { error: deleteError } = await supabase.from('player_tags').delete().eq('id', tagId);

      if (deleteError) throw deleteError;

      setSuccess('Tag deleted successfully!');
      loadTags();
    } catch (err) {
      console.error('Error deleting tag:', err);
      setError(err.message);
    }
  };

  const handleCancel = () => {
    setShowCreateForm(false);
    setEditingTag(null);
    setFormData({
      name: '',
      description: '',
      color: '#3B82F6',
    });
    setError('');
  };

  const handleBulkAssign = (tag) => {
    setSelectedTag(tag);
    setShowBulkAssign(true);
    setError('');
    setSuccess('');
  };

  const getAssignmentCount = (tag) => {
    return tag.tag_assignments?.[0]?.count || 0;
  };

  // Styles
  const containerStyle = {
    padding: SPACING.lg,
  };

  const headerStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: SPACING.lg,
    flexWrap: 'wrap',
    gap: SPACING.md,
  };

  const buttonStyle = {
    padding: `${SPACING.md} ${SPACING.lg}`,
    backgroundColor: COLORS.primary,
    color: COLORS.white,
    border: 'none',
    borderRadius: BORDER_RADIUS.sm,
    cursor: 'pointer',
    fontSize: FONT_SIZE.base,
    fontWeight: '500',
  };

  const secondaryButtonStyle = {
    ...buttonStyle,
    backgroundColor: COLORS.gray[400],
  };

  const dangerButtonStyle = {
    ...buttonStyle,
    backgroundColor: COLORS.danger,
  };

  const formStyle = {
    backgroundColor: COLORS.background.card,
    padding: SPACING.lg,
    borderRadius: BORDER_RADIUS.md,
    boxShadow: SHADOWS.md,
    marginBottom: SPACING.lg,
  };

  const inputGroupStyle = {
    marginBottom: SPACING.md,
  };

  const labelStyle = {
    display: 'block',
    marginBottom: SPACING.sm,
    fontSize: FONT_SIZE.base,
    fontWeight: '500',
    color: COLORS.gray[700],
  };

  const inputStyle = {
    width: '100%',
    padding: SPACING.md,
    fontSize: FONT_SIZE.base,
    border: `1px solid ${COLORS.gray[400]}`,
    borderRadius: BORDER_RADIUS.sm,
    boxSizing: 'border-box',
  };

  const tableStyle = {
    width: '100%',
    borderCollapse: 'collapse',
    backgroundColor: COLORS.white,
    boxShadow: SHADOWS.sm,
  };

  const thStyle = {
    padding: SPACING.md,
    textAlign: 'left',
    borderBottom: `2px solid ${COLORS.gray[300]}`,
    fontSize: FONT_SIZE.base,
    fontWeight: '600',
    color: COLORS.gray[700],
  };

  const tdStyle = {
    padding: SPACING.md,
    borderBottom: `1px solid ${COLORS.gray[300]}`,
    fontSize: FONT_SIZE.base,
  };

  const cardStyle = {
    backgroundColor: COLORS.white,
    padding: SPACING.lg,
    borderRadius: BORDER_RADIUS.md,
    boxShadow: SHADOWS.sm,
    marginBottom: SPACING.md,
  };

  const alertStyle = (type) => ({
    padding: SPACING.md,
    borderRadius: BORDER_RADIUS.sm,
    marginBottom: SPACING.lg,
    backgroundColor: type === 'error' ? COLORS.alert.error.bg : COLORS.alert.success.bg,
    color: type === 'error' ? COLORS.alert.error.text : COLORS.alert.success.text,
    border: `1px solid ${type === 'error' ? COLORS.alert.error.border : COLORS.alert.success.border}`,
  });

  const tagBadgeStyle = (color) => ({
    display: 'inline-block',
    padding: `${SPACING.xs} ${SPACING.md}`,
    backgroundColor: color,
    color: COLORS.white,
    borderRadius: BORDER_RADIUS.full,
    fontSize: FONT_SIZE.sm,
    fontWeight: '500',
  });

  const colorPickerContainerStyle = {
    display: 'grid',
    gridTemplateColumns: 'repeat(5, 1fr)',
    gap: SPACING.sm,
  };

  const colorSwatchStyle = (color, isSelected) => ({
    width: '40px',
    height: '40px',
    backgroundColor: color,
    borderRadius: BORDER_RADIUS.sm,
    cursor: 'pointer',
    border: isSelected ? `3px solid ${COLORS.gray[800]}` : `2px solid ${COLORS.gray[300]}`,
    transition: 'all 0.2s',
  });

  return (
    <div style={containerStyle}>
      <div style={headerStyle}>
        <h2 style={{ margin: 0, fontSize: FONT_SIZE.xl }}>Player Tags</h2>
        <button
          onClick={() => {
            setShowCreateForm(!showCreateForm);
            setEditingTag(null);
            setError('');
          }}
          style={buttonStyle}
        >
          {showCreateForm ? 'Cancel' : '+ Create Tag'}
        </button>
      </div>

      {error && <div style={alertStyle('error')}>{error}</div>}
      {success && <div style={alertStyle('success')}>{success}</div>}

      {showCreateForm && (
        <form onSubmit={handleSubmit} style={formStyle}>
          <h3 style={{ marginTop: 0, fontSize: FONT_SIZE.lg }}>
            {editingTag ? 'Edit Tag' : 'Create New Tag'}
          </h3>

          <div style={inputGroupStyle}>
            <label style={labelStyle}>Tag Name *</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              style={inputStyle}
              placeholder="e.g., Pitcher, Catcher, Varsity"
              required
            />
          </div>

          <div style={inputGroupStyle}>
            <label style={labelStyle}>Description (Optional)</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              style={{ ...inputStyle, minHeight: '80px', resize: 'vertical' }}
              placeholder="Describe what this tag represents..."
            />
          </div>

          <div style={inputGroupStyle}>
            <label style={labelStyle}>Color</label>
            <div style={colorPickerContainerStyle}>
              {colorPalette.map((color) => (
                <div
                  key={color.value}
                  style={colorSwatchStyle(color.value, formData.color === color.value)}
                  onClick={() => setFormData({ ...formData, color: color.value })}
                  title={color.name}
                />
              ))}
            </div>
            <div style={{ marginTop: SPACING.md }}>
              <span style={tagBadgeStyle(formData.color)}>
                Preview: {formData.name || 'Tag Name'}
              </span>
            </div>
          </div>

          <div style={{ display: 'flex', gap: SPACING.md }}>
            <button type="submit" style={buttonStyle}>
              {editingTag ? 'Update Tag' : 'Create Tag'}
            </button>
            <button type="button" onClick={handleCancel} style={secondaryButtonStyle}>
              Cancel
            </button>
          </div>
        </form>
      )}

      {showBulkAssign && selectedTag && (
        <BulkTagAssignment
          tag={selectedTag}
          profile={profile}
          onClose={() => {
            setShowBulkAssign(false);
            setSelectedTag(null);
            loadTags();
          }}
          onSuccess={(message) => {
            setSuccess(message);
            setShowBulkAssign(false);
            setSelectedTag(null);
            loadTags();
          }}
          onError={setError}
        />
      )}

      {loading ? (
        <p>Loading tags...</p>
      ) : tags.length === 0 ? (
        <p style={{ color: COLORS.gray[500] }}>
          No tags yet. Create one to start categorizing your players!
        </p>
      ) : isDesktop ? (
        <table style={tableStyle}>
          <thead>
            <tr>
              <th style={thStyle}>Tag</th>
              <th style={thStyle}>Description</th>
              <th style={thStyle}>Players</th>
              <th style={thStyle}>Created By</th>
              <th style={thStyle}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {tags.map((tag) => (
              <tr key={tag.id}>
                <td style={tdStyle}>
                  <span style={tagBadgeStyle(tag.color)}>{tag.name}</span>
                </td>
                <td style={tdStyle}>{tag.description || '-'}</td>
                <td style={tdStyle}>{getAssignmentCount(tag)}</td>
                <td style={tdStyle}>{tag.created_by_profile?.full_name || 'Unknown'}</td>
                <td style={tdStyle}>
                  <div style={{ display: 'flex', gap: SPACING.sm, flexWrap: 'wrap' }}>
                    <button
                      onClick={() => handleBulkAssign(tag)}
                      style={{ ...buttonStyle, padding: `${SPACING.sm} ${SPACING.md}` }}
                    >
                      Assign
                    </button>
                    <button
                      onClick={() => handleEdit(tag)}
                      style={{ ...secondaryButtonStyle, padding: `${SPACING.sm} ${SPACING.md}` }}
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(tag.id)}
                      style={{
                        ...dangerButtonStyle,
                        padding: `${SPACING.sm} ${SPACING.md}`,
                      }}
                    >
                      Delete
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        // Mobile card view
        <div>
          {tags.map((tag) => (
            <div key={tag.id} style={cardStyle}>
              <div style={{ marginBottom: SPACING.md }}>
                <span style={tagBadgeStyle(tag.color)}>{tag.name}</span>
              </div>
              {tag.description && (
                <p style={{ margin: `${SPACING.xs} 0`, color: COLORS.gray[600] }}>
                  {tag.description}
                </p>
              )}
              <p style={{ margin: `${SPACING.xs} 0`, color: COLORS.gray[600] }}>
                <strong>Players:</strong> {getAssignmentCount(tag)}
              </p>
              <p style={{ margin: `${SPACING.xs} 0`, color: COLORS.gray[600] }}>
                <strong>Created By:</strong> {tag.created_by_profile?.full_name || 'Unknown'}
              </p>
              <div
                style={{
                  display: 'flex',
                  gap: SPACING.sm,
                  marginTop: SPACING.md,
                  flexWrap: 'wrap',
                }}
              >
                <button onClick={() => handleBulkAssign(tag)} style={{ ...buttonStyle, flex: 1 }}>
                  Assign
                </button>
                <button
                  onClick={() => handleEdit(tag)}
                  style={{ ...secondaryButtonStyle, flex: 1 }}
                >
                  Edit
                </button>
                <button
                  onClick={() => handleDelete(tag.id)}
                  style={{ ...dangerButtonStyle, flex: 1 }}
                >
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Bulk Tag Assignment Component
const BulkTagAssignment = ({ tag, profile, onClose, onSuccess, onError }) => {
  const { isDesktop } = useResponsive();
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedPlayers, setSelectedPlayers] = useState(new Set());
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadPlayers();
  }, []);

  const loadPlayers = async () => {
    try {
      setLoading(true);

      // Get all players in organization
      const { data: allPlayers, error: playersError } = await supabase
        .from('profiles')
        .select('id, full_name, email, role')
        .eq('organization_id', profile.organization_id)
        .eq('role', 'player')
        .order('full_name');

      if (playersError) throw playersError;

      // Get players already assigned to this tag
      const { data: assignedPlayers, error: assignedError } = await supabase
        .from('player_tag_assignments')
        .select('player_id')
        .eq('tag_id', tag.id);

      if (assignedError) throw assignedError;

      const assignedSet = new Set(assignedPlayers.map((a) => a.player_id));

      setPlayers(
        allPlayers.map((p) => ({
          ...p,
          hasTag: assignedSet.has(p.id),
        }))
      );

      // Pre-select players who already have the tag
      setSelectedPlayers(assignedSet);
    } catch (err) {
      console.error('Error loading players:', err);
      onError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleTogglePlayer = (playerId) => {
    const newSelected = new Set(selectedPlayers);
    if (newSelected.has(playerId)) {
      newSelected.delete(playerId);
    } else {
      newSelected.add(playerId);
    }
    setSelectedPlayers(newSelected);
  };

  const handleSelectAll = () => {
    if (selectedPlayers.size === players.length) {
      setSelectedPlayers(new Set());
    } else {
      setSelectedPlayers(new Set(players.map((p) => p.id)));
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);

      const {
        data: { user },
      } = await supabase.auth.getUser();

      // Get current assignments
      const { data: currentAssignments } = await supabase
        .from('player_tag_assignments')
        .select('player_id')
        .eq('tag_id', tag.id);

      const currentSet = new Set(currentAssignments?.map((a) => a.player_id) || []);

      // Find players to add (in selectedPlayers but not in currentSet)
      const toAdd = [...selectedPlayers].filter((pid) => !currentSet.has(pid));

      // Find players to remove (in currentSet but not in selectedPlayers)
      const toRemove = [...currentSet].filter((pid) => !selectedPlayers.has(pid));

      // Add new assignments
      if (toAdd.length > 0) {
        const { error: addError } = await supabase.from('player_tag_assignments').insert(
          toAdd.map((playerId) => ({
            player_id: playerId,
            tag_id: tag.id,
            assigned_by: user.id,
          }))
        );

        if (addError) throw addError;
      }

      // Remove assignments
      if (toRemove.length > 0) {
        const { error: removeError } = await supabase
          .from('player_tag_assignments')
          .delete()
          .eq('tag_id', tag.id)
          .in('player_id', toRemove);

        if (removeError) throw removeError;
      }

      onSuccess(`Tag assignments updated! ${toAdd.length} added, ${toRemove.length} removed.`);
    } catch (err) {
      console.error('Error saving assignments:', err);
      onError(err.message);
    } finally {
      setSaving(false);
    }
  };

  // Styles
  const modalOverlayStyle = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000,
    padding: SPACING.md,
  };

  const modalContentStyle = {
    backgroundColor: COLORS.white,
    borderRadius: BORDER_RADIUS.md,
    padding: SPACING.lg,
    maxWidth: '600px',
    width: '100%',
    maxHeight: '80vh',
    overflow: 'auto',
    boxShadow: SHADOWS.xl,
  };

  const buttonStyle = {
    padding: `${SPACING.md} ${SPACING.lg}`,
    backgroundColor: COLORS.primary,
    color: COLORS.white,
    border: 'none',
    borderRadius: BORDER_RADIUS.sm,
    cursor: 'pointer',
    fontSize: FONT_SIZE.base,
    fontWeight: '500',
  };

  const secondaryButtonStyle = {
    ...buttonStyle,
    backgroundColor: COLORS.gray[400],
  };

  const checkboxLabelStyle = {
    display: 'flex',
    alignItems: 'center',
    padding: SPACING.md,
    cursor: 'pointer',
    borderRadius: BORDER_RADIUS.sm,
    transition: 'background-color 0.2s',
  };

  const tagBadgeStyle = {
    display: 'inline-block',
    padding: `${SPACING.xs} ${SPACING.md}`,
    backgroundColor: tag.color,
    color: COLORS.white,
    borderRadius: BORDER_RADIUS.full,
    fontSize: FONT_SIZE.sm,
    fontWeight: '500',
    marginBottom: SPACING.md,
  };

  return (
    <div style={modalOverlayStyle} onClick={onClose}>
      <div style={modalContentStyle} onClick={(e) => e.stopPropagation()}>
        <h3 style={{ marginTop: 0, fontSize: FONT_SIZE.lg }}>
          Assign Players to <span style={tagBadgeStyle}>{tag.name}</span>
        </h3>

        {loading ? (
          <p>Loading players...</p>
        ) : (
          <>
            <div style={{ marginBottom: SPACING.md }}>
              <label style={checkboxLabelStyle}>
                <input
                  type="checkbox"
                  checked={selectedPlayers.size === players.length}
                  onChange={handleSelectAll}
                  style={{ marginRight: SPACING.md }}
                />
                <strong>Select All ({players.length} players)</strong>
              </label>
            </div>

            <div style={{ maxHeight: '400px', overflow: 'auto', marginBottom: SPACING.lg }}>
              {players.map((player) => (
                <label
                  key={player.id}
                  style={{
                    ...checkboxLabelStyle,
                    backgroundColor: selectedPlayers.has(player.id)
                      ? COLORS.background.card
                      : 'transparent',
                  }}
                  onMouseEnter={(e) => {
                    if (!selectedPlayers.has(player.id)) {
                      e.currentTarget.style.backgroundColor = COLORS.gray[100];
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!selectedPlayers.has(player.id)) {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }
                  }}
                >
                  <input
                    type="checkbox"
                    checked={selectedPlayers.has(player.id)}
                    onChange={() => handleTogglePlayer(player.id)}
                    style={{ marginRight: SPACING.md }}
                  />
                  <div>
                    <div>{player.full_name}</div>
                    <div style={{ fontSize: FONT_SIZE.sm, color: COLORS.gray[600] }}>
                      {player.email}
                    </div>
                  </div>
                </label>
              ))}
            </div>

            <div style={{ display: 'flex', gap: SPACING.md, justifyContent: 'flex-end' }}>
              <button
                type="button"
                onClick={onClose}
                style={secondaryButtonStyle}
                disabled={saving}
              >
                Cancel
              </button>
              <button type="button" onClick={handleSave} style={buttonStyle} disabled={saving}>
                {saving ? 'Saving...' : `Save (${selectedPlayers.size} selected)`}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default PlayerTagManager;
